// client/src/ExperimentConfig.js
import React, { useState } from 'react';
import axios from 'axios';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material';
import ConfigChatbot from './ConfigChatbot';
import ConfigADM from './ConfigADM';
import ConfigConversation from './ConfigConversation';
import ConfigStimulusRecommendation from './ConfigADM';
import { useAuth0 } from '@auth0/auth0-react';

// Use environment variable for API URL
const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

function ExperimentConfig() {
	const [experimentType, setExperimentType] = useState('');
	const [experimentURL, setExperimentURL] = useState('');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { isAuthenticated, getAccessTokenSilently } = useAuth0();

	const handleExperimentTypeChange = (event) => {
		setExperimentType(event.target.value);
	};

	const validateAndIncludeAgentFields = (configData) => {
		if (!configData.agents) {
			console.error('Agents data is missing in the configuration.');
			return;
		}

		configData.agents = configData.agents.map(agent => {
			if (!agent.id || !agent.name || !agent.crew_settings) {
				console.error('Missing required agent fields:', agent);
				throw new Error('Agent validation failed. Ensure all agents have id, name, and crew_settings.');
			}
			return agent;
		});

		return configData;
	};

	// Handler to receive configuration data from child components
	const handleConfigSubmit = async (configData) => {
		if (!isAuthenticated) {
			alert('Please login to start the experiment.');
			return;
		}

		try {
			const validatedConfigData = validateAndIncludeAgentFields(configData);
			console.log('sending config data:', validatedConfigData);
			const token = await getAccessTokenSilently();

			// Send validatedConfigData to the server via API call
			axios.post(`${API_URL}/api/experiment/new`, validatedConfigData, {
				headers: {
					Authorization: `Bearer ${token}`,
				}
			})
				.then(response => {
					console.log("Starting Experiment with following settings:");
					console.log(response.data);
					const currentURL = window.location.href; // /experiments/new
					const baseURL = currentURL.substring(0, currentURL.lastIndexOf('/')); // /experiments
					setExperimentURL(`${baseURL}/${response.data.exptID}`); // /experiments/:experimentID
					setIsModalOpen(true);
				})
				.catch(error => {
					console.error('Error launching experiment:', error);
					if (error.response.status === 401) {
						if (!isAuthenticated) {
							alert('You are not authorized to start the experiment. Please login to start the experiment.');
						} else {
							alert("You don't have permission to start the experiment.");
						}
					}
				});
		} catch (error) {
			alert(error.message);
		}
	};

	const handleCopyToClipboard = () => {
		navigator.clipboard.writeText(experimentURL);
		alert('URL copied to clipboard!');
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
	};

	// Function to render the appropriate configuration component
	const renderConfiguration = () => {
		switch (experimentType) {
			case 'chatbot':
				return <ConfigChatbot onSubmit={handleConfigSubmit} />;
			case 'conversation':
				return <ConfigConversation onSubmit={handleConfigSubmit} />;
			case 'stimulus-recommendation':
				return <ConfigStimulusRecommendation onSubmit={handleConfigSubmit} />;
			case 'augmented-decision-making':
				return <ConfigADM onSubmit={handleConfigSubmit} />;
			default:
				return null;
		}
	};

	return (
		<Box mt={4}>
			<FormControl fullWidth>
				<InputLabel id="experiment-type-label">Select Experiment Type</InputLabel>
				<Select
					labelId="experiment-type-label"
					value={experimentType}
					label="Select Experiment Type"
					onChange={handleExperimentTypeChange}>
					<MenuItem value="chatbot">Chatbot</MenuItem>
					<MenuItem value="stimulus-recommendation">Stimulus Recommendation</MenuItem>
					<MenuItem value="conversation">Conversation</MenuItem>
					<MenuItem value="augmented-decision-making">Augmented Decision Making</MenuItem>
				</Select>
			</FormControl>

			{experimentType && (
				<Box mt={4}>
					<Typography variant="h4">Configuration</Typography>
					{renderConfiguration()}
				</Box>
			)}

			<Dialog open={isModalOpen} onClose={handleCloseModal}>
				<DialogTitle>Experiment URL</DialogTitle>
				<DialogContent>
					<a href={experimentURL} target="_blank" rel="noopener noreferrer">
						<Typography variant="body1">{experimentURL}</Typography>
					</a>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCopyToClipboard} color="primary">
						Copy to Clipboard
					</Button>
					<Button onClick={handleCloseModal} color="secondary">
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default ExperimentConfig;